import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack } from "@astryxdesign/core/Layout";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Selector } from "@astryxdesign/core/Selector";
import { Switch } from "@astryxdesign/core/Switch";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { api } from "../../lib/api";
import type { Camera, CameraVendor, CreateCameraInput, StreamQuality, UpdateCameraInput } from "../../lib/api";
import { useIsNarrowViewport } from "../../lib/responsive";

interface CameraFormValues {
  name: string;
  projectId: string;
  location: string;
  vendor: CameraVendor;
  host: string;
  port: string;
  streamPath: string;
  subStreamPath: string;
  streamQuality: StreamQuality;
  username: string;
  password: string;
  ptzEnabled: boolean;
  ptzPort: string;
  ptzUseTls: boolean;
}

type FieldErrors = Partial<Record<keyof CameraFormValues, string>>;

// Mirrors backend usecase/camera_usecase.go's defaultStreamPathFor exactly —
// kept in sync manually since the two run in different languages. Used both
// as the initial value for a new camera and to re-suggest a path when the
// vendor selector changes (see setVendor below). "generic" is deliberately
// "": there's no known convention for a brand not yet in the catalogue, and
// guessing one (e.g. quietly reusing Axis's) would produce a camera that
// looks configured but points at the wrong path — handleSubmit below
// requires an explicit path instead.
const VENDOR_DEFAULT_STREAM_PATH: Record<CameraVendor, string> = {
  axis: "/axis-media/media.amp",
  hikvision: "/Streaming/Channels/101",
  generic: "",
};

// The lower-resolution second stream, where the vendor has a convention for
// one. Only Hikvision does: stream index 02 beside the main stream's 01. Axis
// expresses a smaller view as query parameters on the same path rather than a
// distinct one, so there is nothing to default to and the field stays empty —
// a camera with no second stream is perfectly ordinary.
const VENDOR_DEFAULT_SUB_STREAM_PATH: Record<CameraVendor, string> = {
  axis: "",
  hikvision: "/Streaming/Channels/102",
  generic: "",
};

const VENDOR_OPTIONS: Array<{ value: CameraVendor; label: string }> = [
  { value: "axis", label: "Axis" },
  { value: "hikvision", label: "Hikvision" },
  { value: "generic", label: "Generic / Other" },
];

// Mirrors backend domain/model/camera.go's CameraVendor.HasPTZProtocol.
// PTZ, unlike the RTSP pull, genuinely branches on vendor: Axis speaks VAPIX
// and Hikvision speaks ISAPI, and there is no adapter for anything else, so
// the toggle is disabled rather than offered-and-then-rejected. Typed as a
// full Record so adding a vendor without deciding this is a compile error.
const VENDOR_HAS_PTZ_PROTOCOL: Record<CameraVendor, boolean> = {
  axis: true,
  hikvision: true,
  generic: false,
};

function emptyValues(): CameraFormValues {
  return {
    name: "",
    projectId: "",
    location: "",
    vendor: "axis",
    host: "",
    port: "",
    streamPath: VENDOR_DEFAULT_STREAM_PATH.axis,
    subStreamPath: VENDOR_DEFAULT_SUB_STREAM_PATH.axis,
    streamQuality: "sub",
    username: "",
    password: "",
    ptzEnabled: false,
    ptzPort: "",
    ptzUseTls: false,
  };
}

interface CameraFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present for edit, absent for create. */
  editing: Camera | null;
  isSaving: boolean;
  onCreate: (input: CreateCameraInput) => Promise<unknown>;
  onUpdate: (id: string, input: UpdateCameraInput) => Promise<unknown>;
}

// One dialog handles both create and edit. The two backend operations differ
// in one meaningful way — update needs the row's version for optimistic
// locking, and a blank password there means "leave the stored credential
// alone" rather than "set it to empty" — everything else about the form is
// identical, so splitting this into two components would only duplicate the
// field list.
export function CameraFormDialog({ isOpen, onOpenChange, editing, isSaving, onCreate, onUpdate }: CameraFormDialogProps) {
  const [values, setValues] = useState<CameraFormValues>(emptyValues);
  // Field-level errors mark the offending TextInput/Selector directly
  // (status="error") instead of a single string at the bottom of the form —
  // requestError is reserved for failures the fields themselves can't
  // predict (a 409 from the server after every field already validated).
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string | null>(null);

  // "horizontal" arranges its children as equal-width grid columns and, unlike
  // "horizontal-labels", never collapses on its own. At 375px that gave the
  // Port field roughly 150px to hold a label, a description and an input, and
  // its description clipped. Paired fields are a convenience for a wide
  // dialog, not a requirement of the form, so on a phone they simply stack.
  const pairDirection = useIsNarrowViewport() ? "vertical" : "horizontal";

  // Fetched here (not passed down) so CamerasPage doesn't need to know this
  // dialog needs it — same reasoning as UserFormDialog fetching roles itself.
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: api.projects.list, enabled: isOpen });

  useEffect(() => {
    if (!isOpen) return;
    setFieldErrors({});
    setRequestError(null);
    if (editing) {
      setValues({
        name: editing.name,
        projectId: editing.project_id,
        location: editing.location,
        vendor: editing.vendor,
        host: editing.host,
        port: editing.port ? String(editing.port) : "",
        streamPath: editing.stream_path,
        subStreamPath: editing.sub_stream_path,
        streamQuality: editing.stream_quality,
        username: editing.username,
        password: "",
        ptzEnabled: editing.ptz_enabled,
        ptzPort: editing.ptz_port ? String(editing.ptz_port) : "",
        ptzUseTls: editing.ptz_use_tls,
      });
    } else {
      setValues(emptyValues());
    }
  }, [isOpen, editing]);

  // Default a new camera to the first project once projects load, if
  // nothing has been picked yet.
  useEffect(() => {
    if (!isOpen || editing || values.projectId || !projects || projects.length === 0) return;
    const first = projects[0];
    if (first) setValues((v) => ({ ...v, projectId: first.id }));
  }, [isOpen, editing, projects, values.projectId]);

  const set = <K extends keyof CameraFormValues>(key: K) => (value: string) =>
    setValues((v) => ({ ...v, [key]: value }));

  // Re-suggests the stream path when the vendor changes, but only on CREATE
  // and only when the field still holds a known default (empty, or the
  // *other* vendor's default) — never overwrites something the operator
  // actually typed. Editing an existing camera never touches this, mirroring
  // the backend's own Update(), which deliberately leaves a customized
  // stream path alone on a vendor-only edit (see its doc comment).
  const setVendor = (vendor: string) => {
    setValues((v) => {
      const next = vendor as CameraVendor;
      const isUntouched = v.streamPath.trim() === "" || Object.values(VENDOR_DEFAULT_STREAM_PATH).includes(v.streamPath);
      const streamPath = !editing && isUntouched ? VENDOR_DEFAULT_STREAM_PATH[next] : v.streamPath;
      const subIsUntouched =
        v.subStreamPath.trim() === "" || Object.values(VENDOR_DEFAULT_SUB_STREAM_PATH).includes(v.subStreamPath);
      const subStreamPath = !editing && subIsUntouched ? VENDOR_DEFAULT_SUB_STREAM_PATH[next] : v.subStreamPath;
      // Switching to a vendor with no PTZ adapter clears the flag rather than
      // leaving a now-invalid value in a control the operator can no longer
      // see: the backend rejects that combination outright, so carrying it
      // silently would turn a vendor change into a confusing save failure.
      const ptzEnabled = VENDOR_HAS_PTZ_PROTOCOL[next] ? v.ptzEnabled : false;
      return { ...v, vendor: next, streamPath, subStreamPath, ptzEnabled };
    });
  };

  const statusFor = (field: keyof CameraFormValues) =>
    fieldErrors[field] ? ({ type: "error", message: fieldErrors[field] } as const) : undefined;

  const ptzAvailable = VENDOR_HAS_PTZ_PROTOCOL[values.vendor];

  const handleSubmit = async () => {
    setRequestError(null);

    const errors: FieldErrors = {};
    const port = values.port.trim() === "" ? 0 : Number.parseInt(values.port, 10);
    if (values.port.trim() !== "" && (Number.isNaN(port) || port < 0 || port > 65535)) {
      errors.port = "Must be a number between 0 and 65535 (leave blank for the RTSP default, 554).";
    }
    const ptzPort = values.ptzPort.trim() === "" ? 0 : Number.parseInt(values.ptzPort, 10);
    if (values.ptzPort.trim() !== "" && (Number.isNaN(ptzPort) || ptzPort < 0 || ptzPort > 65535)) {
      errors.ptzPort = "Must be a number between 0 and 65535 (leave blank for the HTTP default, 80).";
    }
    if (!values.host.trim()) errors.host = "Host is required.";
    if (!values.name.trim()) errors.name = "Name is required.";
    if (!values.projectId) errors.projectId = "A project (site) is required.";
    // Axis and Hikvision fall back to a conventional path when left blank —
    // "generic" has none to fall back to (see VENDOR_DEFAULT_STREAM_PATH),
    // so this is the one vendor where the field is actually required.
    if (!values.streamPath.trim() && !VENDOR_DEFAULT_STREAM_PATH[values.vendor]) {
      errors.streamPath = "Stream path is required for a generic camera — there's no known default to fall back to.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (editing) {
        await onUpdate(editing.id, {
          name: values.name.trim(),
          project_id: values.projectId,
          location: values.location.trim(),
          vendor: values.vendor,
          host: values.host.trim(),
          port,
          stream_path: values.streamPath.trim() || VENDOR_DEFAULT_STREAM_PATH[values.vendor],
          sub_stream_path: values.subStreamPath.trim(),
          stream_quality: values.streamQuality,
          username: values.username.trim(),
          // Blank means "keep the stored credential" — see the component doc above.
          password: values.password === "" ? undefined : values.password,
          ptz_enabled: values.ptzEnabled,
          ptz_port: ptzPort,
          ptz_use_tls: values.ptzUseTls,
          version: editing.version,
        });
      } else {
        await onCreate({
          name: values.name.trim(),
          project_id: values.projectId,
          location: values.location.trim(),
          vendor: values.vendor,
          host: values.host.trim(),
          port,
          stream_path: values.streamPath.trim() || VENDOR_DEFAULT_STREAM_PATH[values.vendor],
          sub_stream_path: values.subStreamPath.trim(),
          stream_quality: values.streamQuality,
          username: values.username.trim(),
          password: values.password,
          enabled: true,
          ptz_enabled: values.ptzEnabled,
          ptz_port: ptzPort,
          ptz_use_tls: values.ptzUseTls,
        });
      }
      onOpenChange(false);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  // No <form> element wraps this dialog's fields (Astryx's Layout content/
  // footer slots aren't DOM siblings, so a <form> around one wouldn't reach
  // the other) — TextInput's own onEnter is this design system's answer to
  // the same problem. Guarded on isSaving so holding Enter, or pressing it
  // again before the first request lands, can't fire a second save.
  const submitOnEnter = () => {
    if (!isSaving) void handleSubmit();
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={480}>
      <Layout
        header={
          <DialogHeader
            title={editing ? `Edit ${editing.name}` : "Add camera"}
            subtitle={editing ? undefined : "Credentials are encrypted before they're stored."}
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <FormLayout>
              <TextInput
                label="Name"
                value={values.name}
                onChange={set("name")}
                status={statusFor("name")}
                isRequired
                hasAutoFocus
                onEnter={submitOnEnter}
              />
              <FormLayout direction={pairDirection}>
                <Selector
                  label="Project (site)"
                  value={values.projectId}
                  onChange={set("projectId")}
                  options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
                  placeholder="Choose a project"
                  status={statusFor("projectId")}
                  isRequired
                />
                <Selector
                  label="Camera type"
                  value={values.vendor}
                  onChange={setVendor}
                  options={VENDOR_OPTIONS}
                  isRequired
                />
              </FormLayout>
              <TextInput label="Location" value={values.location} onChange={set("location")} />
              <FormLayout direction={pairDirection}>
                <TextInput
                  label="Host"
                  description="IP address or hostname, no scheme"
                  value={values.host}
                  onChange={set("host")}
                  status={statusFor("host")}
                  isRequired
                  onEnter={submitOnEnter}
                />
                <TextInput
                  label="Port"
                  description="Default 554"
                  value={values.port}
                  onChange={set("port")}
                  status={statusFor("port")}
                  onEnter={submitOnEnter}
                />
              </FormLayout>
              <TextInput
                label="Stream path"
                description={
                  values.vendor === "hikvision"
                    ? "e.g. /Streaming/Channels/101 (channel 1, main stream)"
                    : values.vendor === "generic"
                      ? "No known default for this brand — enter the exact RTSP path"
                      : "e.g. /axis-media/media.amp"
                }
                value={values.streamPath}
                onChange={set("streamPath")}
                status={statusFor("streamPath")}
                isRequired={values.vendor === "generic"}
                onEnter={submitOnEnter}
              />
              <Selector
                label="Stream quality"
                description={
                  values.streamQuality === "sub" && !values.subStreamPath.trim()
                    ? "No sub stream path set, so this camera will still be pulled at full resolution."
                    : "Sub is a fraction of the bandwidth over a site link, and is the right choice for a wall tile. Pin to main where the picture matters more than the cost."
                }
                value={values.streamQuality}
                changeAction={(next) => setValues((v) => ({ ...v, streamQuality: next as StreamQuality }))}
                options={[
                  { value: "sub", label: "Sub stream (recommended)" },
                  { value: "main", label: "Main stream (full resolution)" },
                ]}
              />
              <TextInput
                label="Sub stream path"
                description="Optional. The device's lower-resolution second stream, used for wall tiles — a fraction of the main stream's bandwidth. Leave blank if this camera has only one."
                value={values.subStreamPath}
                onChange={set("subStreamPath")}
                status={statusFor("subStreamPath")}
                onEnter={submitOnEnter}
              />
              <FormLayout direction={pairDirection}>
                <TextInput
                  label="Username"
                  value={values.username}
                  onChange={set("username")}
                  onEnter={submitOnEnter}
                />
                <TextInput
                  label="Password"
                  type="password"
                  description={editing ? "Leave blank to keep the current password" : undefined}
                  value={values.password}
                  onChange={set("password")}
                  onEnter={submitOnEnter}
                />
              </FormLayout>

              {/* A Switch rather than a CheckboxInput despite this being a
                  form: the field reads as a capability the device either has
                  or doesn't, and — unlike the checkboxes elsewhere in this
                  app, which pick items out of a set — flipping it changes
                  what the rest of the form shows (the control port appears).
                  Nothing is applied until Save, same as every other field
                  here. */}
              <Switch
                label="PTZ control"
                description={
                  ptzAvailable
                    ? "This camera has a motorised mount operators can pan, tilt and zoom from the live wall."
                    : "Only Axis and Hikvision cameras can be driven — there's no known control API for a generic brand."
                }
                value={values.ptzEnabled}
                onChange={(ptzEnabled) => setValues((v) => ({ ...v, ptzEnabled }))}
                isDisabled={!ptzAvailable}
                disabledMessage="Only Axis and Hikvision cameras have a supported PTZ control protocol."
              />

              {values.ptzEnabled && (
                <>
                  {/* A camera can be perfectly reachable for video and still
                      refuse control entirely: hardened units close port 80 and
                      answer only on 443. Without this switch there was no way
                      to express that, and every press failed. */}
                  <Switch
                    label="Control API uses HTTPS"
                    description="Turn this on if the camera's web interface is only reachable over https://."
                    value={values.ptzUseTls}
                    onChange={(ptzUseTls) => setValues((v) => ({ ...v, ptzUseTls }))}
                  />
                  <TextInput
                    label="PTZ control port"
                    description={
                      values.ptzUseTls
                        ? "The device's HTTPS port for control commands — not the RTSP port above. Default 443."
                        : "The device's HTTP port for control commands — not the RTSP port above. Default 80."
                    }
                    value={values.ptzPort}
                    onChange={set("ptzPort")}
                    status={statusFor("ptzPort")}
                    onEnter={submitOnEnter}
                  />
                </>
              )}

              {requestError && <Banner status="error" title="Couldn't save camera" description={requestError} />}
            </FormLayout>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} hAlign="end">
              <Button label="Cancel" variant="ghost" onClick={() => onOpenChange(false)} isDisabled={isSaving} />
              <Button
                label={editing ? "Save changes" : "Add camera"}
                variant="primary"
                clickAction={handleSubmit}
                isLoading={isSaving}
              />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
